// ---------------------------------------------------------------------------
// Block Registry — central container for block type definitions.
//
// Shared between ui/ (Konva renderers) and server/ (LLM crystallizer).
// Definitions live in shared/block-registry/definitions/.
// Renderer functions are wired from ui/ at startup (shared/ never imports Konva).
//
// See docs/block-registry-plan.md for the full design.
// ---------------------------------------------------------------------------

/**
 * @typedef {import("./types.js").BlockDefinition} BlockDefinition
 * @typedef {import("./types.js").RendererLayout} RendererLayout
 */

class BlockRegistry {
  constructor() {
    /** @type {Map<string, BlockDefinition>} */
    this._registry = new Map();
  }

  // ── registration ──────────────────────────────────────────────────────

  /**
   * Register a block definition.  Throws if the type is already registered
   * (duplicate registrations are a bug — use registerOrReplace for hot-reload).
   * @param {BlockDefinition} def
   */
  register(def) {
    if (!def || !def.type) throw new Error("BlockDefinition must have a 'type'");
    if (this._registry.has(def.type)) {
      throw new Error(`Block type "${def.type}" is already registered`);
    }
    this._registry.set(def.type, def);
  }

  /**
   * Register or replace — safe for hot-reload / dev environments.
   * @param {BlockDefinition} def
   */
  registerOrReplace(def) {
    if (!def || !def.type) throw new Error("BlockDefinition must have a 'type'");
    this._registry.set(def.type, def);
  }

  // ── lookup ─────────────────────────────────────────────────────────────

  /**
   * Get a block definition by type.  Throws if not found.
   * @param {string} type
   * @returns {BlockDefinition}
   */
  get(type) {
    const def = this._registry.get(type);
    if (!def) throw new Error(`Unknown block type: "${type}"`);
    return def;
  }

  /**
   * Get a block definition by type, or undefined if not registered.
   * Used during migration as a fallback bridge — caller tries registry first,
   * falls back to the old switch statement.
   * @param {string} type
   * @returns {BlockDefinition|undefined}
   */
  getIfRegistered(type) {
    return this._registry.get(type);
  }

  /**
   * Check if a block type is registered.
   * @param {string} type
   * @returns {boolean}
   */
  has(type) {
    return this._registry.has(type);
  }

  // ── iteration ──────────────────────────────────────────────────────────

  /**
   * List all registered block types.
   * @returns {string[]}
   */
  listTypes() {
    return [...this._registry.keys()];
  }

  /**
   * List all registered definitions.
   * @returns {BlockDefinition[]}
   */
  listAll() {
    return [...this._registry.values()];
  }

  // ── layout queries ─────────────────────────────────────────────────────

  /**
   * Get layout metadata for a block type.  Returns sensible defaults for
   * unregistered types (container-sized, non-stretchable).
   * @param {string} type
   * @returns {RendererLayout}
   */
  getLayoutInfo(type) {
    const def = this._registry.get(type);
    if (!def || !def.renderer || !def.renderer.layout) {
      return { isContentSized: false, canStretch: false };
    }
    return def.renderer.layout;
  }

  /**
   * Get the debug overlay color for a block type.
   * @param {string} type
   * @returns {string}
   */
  getDebugColor(type) {
    const def = this._registry.get(type);
    return (def && def.renderer && def.renderer.debugColor) || "rgba(128,128,128,0.03)";
  }

  // ── data queries ───────────────────────────────────────────────────────

  /**
   * Check if a block has crystallized data worth displaying.
   * Delegates to renderer.hasData if set, otherwise uses the generic default.
   * @param {string} type
   * @param {object} blockDef
   * @param {object} crystalizedData
   * @returns {boolean}
   */
  hasData(type, blockDef, crystalizedData) {
    const def = this._registry.get(type);
    if (def && def.renderer && typeof def.renderer.hasData === "function") {
      return def.renderer.hasData(blockDef, crystalizedData);
    }
    // Generic default
    const d = crystalizedData[blockDef.id];
    if (d == null) return false;
    if (Array.isArray(d)) return d.length > 0;
    if (typeof d === "object") return Object.values(d).some(v => v != null && v !== "");
    return d !== "";
  }

  // ── editor queries ─────────────────────────────────────────────────────

  /**
   * Get the editor palette — a lightweight list of all blocks with editor
   * metadata suitable for a block-picker UI.
   * @returns {object[]}
   */
  getEditorPalette() {
    const palette = [];
    for (const def of this._registry.values()) {
      if (!def.editor) continue;
      const entry = {
        type: def.type,
        label: def.label,
        description: def.description || "",
        icon: def.editor.icon,
        category: def.editor.category,
        defaultTemplate: def.editor.defaultTemplate,
      };
      // Surface sub-type options if present
      if (def.subTypes) {
        entry.subTypes = {};
        for (const [key, sub] of Object.entries(def.subTypes)) {
          if (typeof sub.list === "function") {
            entry.subTypes[key] = sub.list();
          }
        }
      }
      palette.push(entry);
    }
    return palette;
  }

  /**
   * Get the editor config schema for a block type — the configurable fields
   * for the block's editor panel.
   * @param {string} type
   * @returns {object[]|undefined}
   */
  getConfigSchema(type) {
    const def = this._registry.get(type);
    if (!def || !def.editor) return undefined;
    return def.editor.configFields;
  }

  // ── DSL schema ─────────────────────────────────────────────────────────

  /**
   * Get an aggregated DSL schema (oneOf all block types' dslSchema).
   * Used for validating DSL documents.
   * @returns {object}
   */
  getDSLSchema() {
    const schemas = [];
    for (const def of this._registry.values()) {
      if (def.dslSchema) schemas.push(def.dslSchema);
    }
    return { oneOf: schemas };
  }

  // ── sub-type queries ──────────────────────────────────────────────────

  /**
   * Check if a block type has a named sub-type registry.
   * @param {string} type
   * @param {string} field
   * @returns {boolean}
   */
  hasSubType(type, field) {
    const def = this._registry.get(type);
    return !!(def && def.subTypes && def.subTypes[field]);
  }

  /**
   * Get a sub-type registry by name.
   * @param {string} type
   * @param {string} field
   * @returns {object|undefined}
   */
  getSubTypeRegistry(type, field) {
    const def = this._registry.get(type);
    if (!def || !def.subTypes) return undefined;
    return def.subTypes[field];
  }

  // ── helpers ────────────────────────────────────────────────────────────

  /**
   * Create a sub-registry for polymorphic blocks (chart kind, bullet style, etc.)
   * @returns {object}
   */
  static createSubRegistry() {
    const map = new Map();
    return {
      register(def) {
        if (!def || !def.name) throw new Error("Sub-type must have a 'name'");
        map.set(def.name, def);
      },
      get(name) {
        const d = map.get(name);
        if (!d) throw new Error(`Unknown sub-type: "${name}"`);
        return d;
      },
      getIfRegistered(name) {
        return map.get(name);
      },
      list() {
        return [...map.keys()];
      },
      has(name) {
        return map.has(name);
      },
    };
  }
}

// Singleton instance
const blockRegistry = new BlockRegistry();

export { blockRegistry, BlockRegistry };
export default blockRegistry;
